import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  ChevronDown,
  Crosshair,
  Edit2,
  FileText,
  Images,
  Info,
  Settings,
  Trophy,
  Users,
} from 'lucide-react';
import { useApiJson } from '../../hooks/useApi';
import { apiFetch } from '../../lib/api';
import {
  ADMIN_TAB_LABELS,
  readAdminTabOrder,
  validAdminTabOrder,
  type AdminContentTab,
  type AdminTabId,
} from '../../lib/admin-tab-order';
import { cn } from '../../lib/utils';
import { AdminTabOrderList } from './AdminTabOrderList';

const entries = {
  rifle: Crosshair,
  events: Calendar,
  gallery: Images,
  pages: FileText,
  quizzes: Trophy,
  banner: Edit2,
  about: Info,
  leadership: Users,
};
const endpoint = '/api/settings/adminTabOrder';
const button =
  'inline-flex min-h-11 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 text-sm font-medium disabled:opacity-40';
const groups: { label: string; tabs: readonly AdminTabId[] }[] = [
  { label: 'Events & Activities', tabs: ['events', 'rifle', 'quizzes'] },
  {
    label: 'Website Content',
    tabs: ['about', 'leadership', 'pages', 'banner'],
  },
  { label: 'Gallery', tabs: ['gallery'] },
];

export function AdminTabs({
  activeTab,
  onSelect,
  children,
}: {
  activeTab: AdminContentTab;
  onSelect: (tab: AdminContentTab) => void;
  children?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const selectId = useId();
  const {
    data,
    loading,
    error: loadError,
    mutate,
  } = useApiJson<{ order: unknown }>(endpoint);
  const [confirmed, setConfirmed] = useState<AdminTabId[] | null>(null);
  const [draft, setDraft] = useState<AdminTabId[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const editor = useRef<HTMLElement>(null);
  const arrange = useRef<HTMLButtonElement>(null);
  const settingsButton = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  const order = confirmed ?? readAdminTabOrder(data?.order);
  const editing = draft !== null;
  const grouped = groups.map((group) => ({
    ...group,
    tabs: order.filter((id) => group.tabs.includes(id)),
  }));

  useEffect(() => {
    if (editing) editor.current?.focus();
    else if (wasEditing.current)
      (settingsOpen ? arrange.current : settingsButton.current)?.focus();
    wasEditing.current = editing;
  }, [editing, settingsOpen]);

  function close() {
    setDraft(null);
    setError(null);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!draft || saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = await apiFetch<{ order: unknown }>(endpoint, {
        method: 'PUT',
        body: JSON.stringify({ order: draft }),
      });
      if (!validAdminTabOrder(body.order))
        throw new Error(
          'Could not confirm the saved tab order. Please try again.',
        );
      setConfirmed([...body.order]);
      setSaved(true);
      close();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not save the tab order. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid min-w-0 items-start gap-5 xl:grid-cols-[208px_minmax(0,1fr)] xl:gap-6">
      <aside className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-end gap-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)] p-3 xl:block xl:space-y-3">
        <nav aria-label="Admin sections">
          <div className="xl:hidden">
            <label
              htmlFor={selectId}
              className="mb-1.5 block text-xs font-semibold text-[var(--color-text-secondary)]"
            >
              Admin section
            </label>
            <div className="relative">
              <select
                id={selectId}
                aria-label="Admin section"
                value={activeTab}
                onChange={(event) => {
                  const id = event.target.value as AdminTabId;
                  if (id === 'rifle') navigate('/admin/scoring');
                  else onSelect(id);
                }}
                className="min-h-11 w-full appearance-none rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-0)] py-2 pl-3 pr-10 text-base font-semibold text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
              >
                {grouped.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.tabs.map((id) => (
                      <option key={id} value={id}>
                        {ADMIN_TAB_LABELS[id]}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronDown
                aria-hidden="true"
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-secondary)]"
              />
            </div>
          </div>
          <div className="hidden space-y-5 xl:block">
            {grouped.map((group) => (
              <section key={group.label} aria-label={group.label}>
                <h2 className="mb-1.5 px-2 text-xs font-semibold text-[var(--color-text-secondary)]">
                  {group.label}
                </h2>
                <div className="space-y-1">
                  {group.tabs.map((id) => {
                    const Icon = entries[id];
                    const label = ADMIN_TAB_LABELS[id];
                    const className = cn(
                      'relative flex min-h-11 w-full items-center gap-2 rounded-[var(--radius-md)] px-2.5 py-2.5 text-left text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
                      id === activeTab
                        ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                        : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text-primary)]',
                    );
                    return id === 'rifle' ? (
                      <Link
                        key={id}
                        data-admin-tab={id}
                        to="/admin/scoring"
                        className={className}
                      >
                        <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                        {label}
                      </Link>
                    ) : (
                      <button
                        key={id}
                        type="button"
                        data-admin-tab={id}
                        aria-pressed={id === activeTab}
                        onClick={() => onSelect(id)}
                        className={className}
                      >
                        <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </nav>
        <button
          ref={settingsButton}
          type="button"
          aria-expanded={settingsOpen}
          aria-controls={`${selectId}-settings`}
          onClick={() => setSettingsOpen((open) => !open)}
          title="Settings"
          className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-[var(--radius-md)] px-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] xl:w-full xl:justify-start xl:border-t xl:border-[var(--color-border)]"
        >
          <Settings aria-hidden="true" className="h-4 w-4" />
          <span className="sr-only xl:not-sr-only">Settings</span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'ml-auto hidden h-3.5 w-3.5 xl:block',
              settingsOpen && 'rotate-180',
            )}
          />
        </button>
        <div
          id={`${selectId}-settings`}
          hidden={!settingsOpen}
          className="col-span-2 pt-1"
        >
          <button
            ref={arrange}
            type="button"
            className={cn(button, 'w-full')}
            disabled={
              !data || loading || Boolean(loadError) || saving || editing
            }
            onClick={() => {
              setDraft([...order]);
              setError(null);
              setSaved(false);
            }}
          >
            Arrange tabs
          </button>
        </div>
        {saved ? (
          <p
            role="status"
            className="col-span-2 text-xs text-[var(--color-text-secondary)]"
          >
            Tab order saved for all admins.
          </p>
        ) : null}
        {loadError ? (
          <p
            role="alert"
            className="col-span-2 text-sm text-[var(--color-error-text)]"
          >
            Could not load the shared tab order.{' '}
            <button
              type="button"
              className={button}
              onClick={() => void mutate()}
            >
              Retry
            </button>
          </p>
        ) : null}
      </aside>
      <div className="min-w-0 space-y-5">
        {draft ? (
          <section
            ref={editor}
            tabIndex={-1}
            aria-label="Arrange admin tabs"
            className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)] p-3 sm:p-4"
          >
            <h2 className="font-semibold">Arrange tabs</h2>
            <p className="text-sm text-[var(--color-text-secondary)]">
              Hold the three-line handle and drag. Save when finished. This
              changes the order within each group for all admins.
            </p>
            <form onSubmit={save} className="space-y-3">
              <AdminTabOrderList
                order={draft}
                onReorder={setDraft}
                disabled={saving}
              />
              {error ? (
                <p
                  role="alert"
                  className="text-sm text-[var(--color-error-text)]"
                >
                  {error}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className={cn(
                    button,
                    'border-transparent bg-[var(--color-accent)] text-white',
                  )}
                >
                  {saving ? 'Saving…' : 'Save order'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className={button}
                  onClick={close}
                >
                  Cancel
                </button>
              </div>
            </form>
          </section>
        ) : null}
        {children}
      </div>
    </div>
  );
}
